import { createLogger } from "../utils/logger.js";
//#region ../src/state/recording-options.ts
/**
* Recording Options - User-configurable settings for data capture.
*
* Allows users to disable/configure high-frequency data streams
* (depth sampling, image capture) to improve performance on lower-end devices.
*
* Options persist in localStorage across sessions.
*/
const log = createLogger("RecordingOptions");
/**
* localStorage key for persisted options.
*
* **Multi-tab caveat:** All tabs/instances sharing the same origin will
* read and write this key. In multi-tab or embedded scenarios, concurrent
* saves can silently overwrite each other. Use a custom `storageKey`
* parameter in `loadRecordingOptions` / `saveRecordingOptions` to isolate
* instances when needed.
*/
const STORAGE_KEY = "gps-plus-slam-recorder-options";
/** Default recording options (all streams enabled) */
const DEFAULT_RECORDING_OPTIONS = {
	depth: {
		enabled: true,
		intervalMs: 1e3,
		gridSize: 3
	},
	images: {
		enabled: true,
		intervalMs: 2e3,
		quality: .7,
		resolutionDivisor: 1
	},
	arCrashIsolation: {
		enableDomOverlay: true,
		enableCameraAccess: true,
		enableDepthSensingFeature: true,
		enableCss3dRenderer: true,
		enableCameraTextureAcquisition: true,
		applyChromiumProjectionLayerWorkaround: true
	}
};
/** Validation constraints for depth options */
const DEPTH_CONSTRAINTS = {
	intervalMs: {
		min: 500,
		max: 5e3,
		step: 100
	},
	gridSize: {
		min: 2,
		max: 10,
		step: 1
	}
};
/** Validation constraints for image options */
const IMAGE_CONSTRAINTS = {
	intervalMs: {
		min: 1e3,
		max: 1e4,
		step: 500
	},
	quality: {
		min: .3,
		max: 1,
		step: .1
	},
	resolutionDivisor: {
		min: 1,
		max: 8,
		step: 1
	}
};
/**
* Validate and normalize AR crash isolation flags.
* Missing or invalid values fall back to defaults.
*/
function validateArCrashIsolationOptions(options) {
	const defaults = DEFAULT_RECORDING_OPTIONS.arCrashIsolation;
	return {
		enableDomOverlay: typeof options.enableDomOverlay === "boolean" ? options.enableDomOverlay : defaults.enableDomOverlay,
		enableCameraAccess: typeof options.enableCameraAccess === "boolean" ? options.enableCameraAccess : defaults.enableCameraAccess,
		enableDepthSensingFeature: typeof options.enableDepthSensingFeature === "boolean" ? options.enableDepthSensingFeature : defaults.enableDepthSensingFeature,
		enableCss3dRenderer: typeof options.enableCss3dRenderer === "boolean" ? options.enableCss3dRenderer : defaults.enableCss3dRenderer,
		enableCameraTextureAcquisition: typeof options.enableCameraTextureAcquisition === "boolean" ? options.enableCameraTextureAcquisition : defaults.enableCameraTextureAcquisition,
		applyChromiumProjectionLayerWorkaround: typeof options.applyChromiumProjectionLayerWorkaround === "boolean" ? options.applyChromiumProjectionLayerWorkaround : defaults.applyChromiumProjectionLayerWorkaround
	};
}
/**
* Clamp a value to the specified constraints.
*/
function clamp(value, min, max) {
	return Math.max(min, Math.min(max, value));
}
/**
* Validate and normalize depth options.
* Invalid values are clamped to valid ranges.
*/
function validateDepthOptions(options) {
	const defaults = DEFAULT_RECORDING_OPTIONS.depth;
	return {
		enabled: typeof options.enabled === "boolean" ? options.enabled : defaults.enabled,
		intervalMs: clamp(typeof options.intervalMs === "number" ? options.intervalMs : defaults.intervalMs, DEPTH_CONSTRAINTS.intervalMs.min, DEPTH_CONSTRAINTS.intervalMs.max),
		gridSize: clamp(typeof options.gridSize === "number" ? options.gridSize : defaults.gridSize, DEPTH_CONSTRAINTS.gridSize.min, DEPTH_CONSTRAINTS.gridSize.max)
	};
}
/**
* Validate and normalize image options.
* Invalid values are clamped to valid ranges.
*/
function validateImageOptions(options) {
	const defaults = DEFAULT_RECORDING_OPTIONS.images;
	return {
		enabled: typeof options.enabled === "boolean" ? options.enabled : defaults.enabled,
		intervalMs: clamp(typeof options.intervalMs === "number" ? options.intervalMs : defaults.intervalMs, IMAGE_CONSTRAINTS.intervalMs.min, IMAGE_CONSTRAINTS.intervalMs.max),
		quality: clamp(typeof options.quality === "number" ? options.quality : defaults.quality, IMAGE_CONSTRAINTS.quality.min, IMAGE_CONSTRAINTS.quality.max),
		resolutionDivisor: clamp(typeof options.resolutionDivisor === "number" ? options.resolutionDivisor : defaults.resolutionDivisor, IMAGE_CONSTRAINTS.resolutionDivisor.min, IMAGE_CONSTRAINTS.resolutionDivisor.max)
	};
}
/**
* Validate and normalize a full RecordingOptions object.
* Merges with defaults and clamps invalid values.
*/
function validateRecordingOptions(options) {
	return {
		depth: validateDepthOptions(options.depth ?? {}),
		images: validateImageOptions(options.images ?? {}),
		arCrashIsolation: validateArCrashIsolationOptions(options.arCrashIsolation ?? {})
	};
}
/**
* Load recording options from localStorage.
* Returns defaults if no saved options exist or parsing fails.
* Validates and merges with defaults to handle schema evolution.
* @param storageKey - Optional custom localStorage key (defaults to STORAGE_KEY).
*/
function loadRecordingOptions(storageKey = STORAGE_KEY) {
	try {
		const stored = localStorage.getItem(storageKey);
		if (stored) {
			const parsed = JSON.parse(stored);
			log.debug("Loaded options from storage:", parsed);
			return validateRecordingOptions(parsed);
		}
	} catch (err) {
		log.warn("Failed to load recording options:", err);
	}
	log.debug("Using default recording options");
	return cloneRecordingOptions(DEFAULT_RECORDING_OPTIONS);
}
/**
* Save recording options to localStorage.
* Options are validated before saving.
* @param storageKey - Optional custom localStorage key (defaults to STORAGE_KEY).
*/
function saveRecordingOptions(options, storageKey = STORAGE_KEY) {
	try {
		const validated = validateRecordingOptions(options);
		localStorage.setItem(storageKey, JSON.stringify(validated));
		log.debug("Saved recording options:", validated);
	} catch (err) {
		log.warn("Failed to save recording options:", err);
	}
}
/**
* Reset recording options to defaults.
* Clears localStorage and returns default options.
* @param storageKey - Optional custom localStorage key (defaults to STORAGE_KEY).
*/
function resetRecordingOptions(storageKey = STORAGE_KEY) {
	try {
		localStorage.removeItem(storageKey);
		log.debug("Reset recording options to defaults");
	} catch (err) {
		log.warn("Failed to clear recording options from storage:", err);
	}
	return cloneRecordingOptions(DEFAULT_RECORDING_OPTIONS);
}
/**
* Create a deep copy of recording options.
* Useful for creating mutable copies of the frozen defaults.
*/
function cloneRecordingOptions(options) {
	return {
		depth: { ...options.depth },
		images: { ...options.images },
		arCrashIsolation: { ...options.arCrashIsolation }
	};
}
//#endregion
export { DEFAULT_RECORDING_OPTIONS, DEPTH_CONSTRAINTS, IMAGE_CONSTRAINTS, STORAGE_KEY, cloneRecordingOptions, loadRecordingOptions, resetRecordingOptions, saveRecordingOptions, validateArCrashIsolationOptions, validateDepthOptions, validateImageOptions, validateRecordingOptions };
